import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MapPinned, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdminCityPricing() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/superadmin/city-prices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Prix SaaS par ville</h1>
          <p className="text-sm text-slate-500">Adapter les tarifs d'abonnement selon la ville où l'agence est basée</p>
        </div>
        <NewPriceDialog onCreated={load} />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="text-left p-3">Pays</th>
                <th className="text-left">Ville</th>
                <th className="text-left">Plan</th>
                <th className="text-right">Mensuel</th>
                <th className="text-right">Annuel</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {items.map((c) => (
                <tr key={c.id} data-testid={`sa-cp-row-${c.id}`}>
                  <td className="p-3">{c.country}</td>
                  <td className="flex items-center gap-2 p-3"><MapPinned className="w-4 h-4 text-orange-500" /> {c.city}</td>
                  <td><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 font-semibold">{c.plan}</span></td>
                  <td className="text-right">{formatCurrency(c.price_monthly, c.currency)}</td>
                  <td className="text-right">{formatCurrency(c.price_annual, c.currency)}</td>
                  <td className="p-3 text-right">
                    <Button variant="ghost" size="icon" onClick={async () => {
                      if (!confirm("Supprimer ?")) return;
                      await api.delete(`/superadmin/city-prices/${c.id}`); toast.success("Supprimé"); load();
                    }} data-testid={`sa-cp-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Aucun prix ville configuré (les prix par défaut s'appliquent)</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function NewPriceDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ country: "Mali", city: "", plan: "BASIC", price_monthly: "", price_annual: "", currency: "FCFA" });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="btn-pill bg-orange-500 hover:bg-orange-600" data-testid="sa-cp-add-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau prix</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Prix SaaS par ville</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Pays</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="sa-cp-country" /></div>
          <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="sa-cp-city" /></div>
          <div><Label>Plan</Label>
            <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
              <SelectTrigger data-testid="sa-cp-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="FREE">FREE</SelectItem>
                <SelectItem value="BASIC">BASIC</SelectItem>
                <SelectItem value="PRO">PRO</SelectItem>
                <SelectItem value="BUSINESS">BUSINESS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Devise</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
          <div><Label>Mensuel</Label><Input type="number" value={form.price_monthly} onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} data-testid="sa-cp-monthly" /></div>
          <div><Label>Annuel</Label><Input type="number" value={form.price_annual} onChange={(e) => setForm({ ...form, price_annual: e.target.value })} data-testid="sa-cp-annual" /></div>
        </div>
        <Button className="btn-pill bg-orange-500 hover:bg-orange-600 w-full mt-3" data-testid="sa-cp-save-btn" onClick={async () => {
          if (!form.city) { toast.error("Ville requise"); return; }
          try { await api.post("/superadmin/city-prices", { ...form, price_monthly: Number(form.price_monthly), price_annual: Number(form.price_annual) }); toast.success("Prix enregistré"); setOpen(false); onCreated(); }
          catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        }}>Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
}
